/**
 * 天地图 JS API 动态加载器。
 *
 * 用法：
 *   import { loadTianditu, getMapConfig } from '@/utils/tianditu'
 *   loadTianditu().then(({ T, config }) => {
 *     const map = new T.Map(dom)
 *     map.centerAndZoom(new T.LngLat(116.4, 39.9), 13)
 *   })
 *
 * 设计要点：
 *  - key/版本/接口地址都来自后端配置中心 MAP_DOMAIN（见 mapConfigApi.js）。
 *  - 全局只加载一次，多次调用复用同一个 Promise。
 *  - 加载失败时返回带提示的 reject，上层组件可统一弹错。
 */
import { listMapConfig } from '@/api/system/mapConfigApi'

let scriptPromise = null
let cachedConfig = null

const DEFAULT_CONFIG = {
  MAP_TYPE: 'TIANDITU',
  TIANDITU_API_URL: 'https://api.tianditu.gov.cn/api',
  TIANDITU_VERSION: '4.0',
  TIANDITU_KEY: '',
  STORE_COORD: 'GCJ02'
}

export function getMapConfig() {
  if (cachedConfig) {
    return Promise.resolve(cachedConfig)
  }
  return listMapConfig()
    .then(remote => {
      const merged = { ...DEFAULT_CONFIG, ...(remote || {}) }
      // 只有真正拿到 key 才缓存，否则下一次还可以重试
      if (merged.TIANDITU_KEY && String(merged.TIANDITU_KEY).trim()) {
        cachedConfig = merged
      }
      return merged
    })
    .catch(() => {
      return { ...DEFAULT_CONFIG }
    })
}

/**
 * 强制刷新配置缓存（修改配置中心后无需刷新页面也可重新拉取）。
 */
export function clearMapConfigCache() {
  cachedConfig = null
  scriptPromise = null
}

/**
 * 加载天地图 JS API。
 * @returns {Promise<{T:object, config:object}>}
 */
export function loadTianditu() {
  if (typeof window !== 'undefined' && window.T && window.T.Map) {
    return getMapConfig().then(config => ({ T: window.T, config }))
  }
  if (scriptPromise) {
    return scriptPromise
  }
  scriptPromise = getMapConfig().then(
    config =>
      new Promise((resolve, reject) => {
        const key = (config.TIANDITU_KEY || '').trim()
        if (!key) {
          scriptPromise = null
          reject(new Error('天地图 key 未配置，请在「配置 → 配置中心 → MAP_DOMAIN」补充 TIANDITU_KEY'))
          return
        }
        const base = config.TIANDITU_API_URL || DEFAULT_CONFIG.TIANDITU_API_URL
        const version = config.TIANDITU_VERSION || DEFAULT_CONFIG.TIANDITU_VERSION
        const src = `${base}?v=${encodeURIComponent(version)}&tk=${encodeURIComponent(key)}`

        const script = document.createElement('script')
        script.type = 'text/javascript'
        script.src = src
        script.async = true
        script.onload = () => {
          if (window.T && window.T.Map) {
            resolve({ T: window.T, config })
          } else {
            reject(new Error('天地图 SDK 加载完成但未注册 window.T'))
          }
        }
        script.onerror = () => {
          scriptPromise = null
          reject(new Error('天地图 SDK 加载失败，请检查网络或 key 是否有效'))
        }
        document.head.appendChild(script)
      })
  )
  return scriptPromise
}
